// Persistent right-side dock shown on viewports >= xl. Hosts the session
// panels as tabs (Contexto, Minimapa) with no dimming backdrop, and — when a
// citation or investigation is opened — a pushed detail view with back +
// expand controls. The tab/detail contents are the same components used by the
// lightbox sheets below xl, so there is a single source of truth per panel.
// The detail body is injected by the caller (`detailContent`) so the dock stays
// decoupled from the source/research fetch + render details.

import { ChevronLeft, Maximize2, X } from "lucide-react";
import type { JSX, ReactNode } from "react";

import { Tabs } from "@/components/ui/tabs";
import type { DockDetail, DockTab } from "@/lib/use-right-dock";

import type { MinimapItem } from "./Minimap";
import { MinimapTimeline } from "./MinimapTimeline";
import { SessionContextContent } from "./SessionContextSheet";

export type { DockTab };

const DOCK_TABS: { value: DockTab; label: string }[] = [
  { value: "contexto", label: "Contexto" },
  { value: "minimapa", label: "Minimapa" },
];

interface RightDockProps {
  tab: DockTab;
  onTabChange: (tab: DockTab) => void;
  onClose: () => void;
  sessionId: string | null;
  minimapItems: MinimapItem[];
  activeMinimapId?: string | null;
  onJumpTo: (itemId: string) => void;
  /** When set, the dock shows this detail (pushed over the tabs). */
  detail: DockDetail | null;
  /** The detail body, built by the caller (source/research content). */
  detailContent: ReactNode;
  onDetailBack: () => void;
  onDetailExpand: () => void;
}

const CONTROL_CLASS =
  "rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

export function RightDock({
  tab,
  onTabChange,
  onClose,
  sessionId,
  minimapItems,
  activeMinimapId,
  onJumpTo,
  detail,
  detailContent,
  onDetailBack,
  onDetailExpand,
}: RightDockProps): JSX.Element {
  if (detail) {
    const title =
      detail.kind === "source"
        ? "Fuente"
        : detail.kind === "research"
          ? "Investigación"
          : "Informe";
    return (
      <div className="flex h-full flex-col">
        <div className="flex items-center gap-1 border-b border-border px-2 py-1.5">
          <button
            type="button"
            onClick={onDetailBack}
            aria-label="Volver"
            className="flex items-center gap-1 rounded-md py-1 pl-1 pr-2 text-body-3 text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <ChevronLeft size={16} />
            Volver
          </button>
          <span className="min-w-0 flex-1 truncate text-center text-body-3 font-medium text-foreground">
            {title}
          </span>
          <button
            type="button"
            onClick={onDetailExpand}
            aria-label="Expandir"
            className={CONTROL_CLASS}
          >
            <Maximize2 size={16} />
          </button>
        </div>
        <div
          className={
            detail.kind === "report" ? "flex-1 overflow-y-auto" : "flex-1 overflow-y-auto px-4 py-4"
          }
        >
          {detailContent}
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-stretch">
        <Tabs
          tabs={DOCK_TABS}
          value={tab}
          onValueChange={(value) => {
            if (value === "contexto" || value === "minimapa") onTabChange(value);
          }}
          className="flex-1"
        />
        <div className="flex items-center border-b border-border pl-1 pr-2">
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar panel"
            className={CONTROL_CLASS}
          >
            <X size={16} />
          </button>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto px-4 py-4">
        {tab === "contexto" ? (
          <SessionContextContent sessionId={sessionId} enabled />
        ) : (
          <MinimapTimeline
            items={minimapItems}
            activeItemId={activeMinimapId ?? null}
            onJumpTo={onJumpTo}
          />
        )}
      </div>
    </div>
  );
}
